import type { Middleware } from '@reduxjs/toolkit';
import { NEW_NOTIFICATION } from '@reduxLocal/actions/types';
import { areNotificationsDuplicate } from '@utils/notifications/notificationUtils';

const MAX_LIST_SCAN = 200;
const recentNotifications: unknown[] = [];

/**
 * Drops duplicate {@link NEW_NOTIFICATION} dispatches before they hit reducers or `lastAction`.
 * The server can emit the same data change multiple times (e.g. company roster + owner path,
 * duplicate socket IDs in Redis), which previously flooded Redux and the notifications UI.
 */
export const notificationDedupMiddleware: Middleware =
	(store) => (next) => (action: unknown) => {
		if (
			!action ||
			typeof action !== 'object' ||
			(action as { type?: string }).type !== NEW_NOTIFICATION
		) {
			return next(action);
		}

		const payload = (action as { payload?: unknown }).payload;
		if (!payload || typeof payload !== 'object') {
			return next(action);
		}

		const notifState = (store.getState() as { notifications?: { lastNotification?: unknown } })
			.notifications;
		const last = notifState?.lastNotification;

		if (
			last &&
			typeof last === 'object' &&
			Object.keys(last as object).length > 0 &&
			areNotificationsDuplicate(last as any, payload as any)
		) {
			return undefined;
		}

		for (let i = 0; i < recentNotifications.length && i < MAX_LIST_SCAN; i++) {
			const existing = recentNotifications[i];
			if (
				existing &&
				typeof existing === 'object' &&
				areNotificationsDuplicate(existing as any, payload as any)
			) {
				return undefined;
			}
		}

		recentNotifications.unshift(payload);
		if (recentNotifications.length > MAX_LIST_SCAN) {
			recentNotifications.pop();
		}

		return next(action);
	};
